import type { FieldValues, InputDef } from "./types";

/**
 * Client-side validation. Mirrors the server rules exactly — the server is the
 * authority, this exists so the user finds out before they spend a run unit.
 *
 * Field-agnostic by construction: it reads InputDef and nothing else. If this
 * file ever needs to know a field id, the abstraction has failed.
 */

export type Errors = Record<string, string>;

/** An input the user can't see isn't an input. Hidden ones never block submit
 *  and never get sent. */
export function isVisible(input: InputDef, values: FieldValues): boolean {
  return input.showIf ? input.showIf(values) : true;
}

export function visibleInputs(inputs: InputDef[], values: FieldValues): InputDef[] {
  return inputs.filter((i) => isVisible(i, values));
}

function isEmpty(v: FieldValues[string]): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** Arabic digits in, Latin digits out — users type ٤٠ and mean 40. */
export function normalizeDigits(s: string): string {
  return s
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
}

/** Validate one input against its own rules. Returns an Arabic message or null. */
export function validateInput(input: InputDef, values: FieldValues): string | null {
  if (!isVisible(input, values)) return null;

  const value = values[input.name];

  if (isEmpty(value)) {
    if (input.required) {
      switch (input.type) {
        case "select":
        case "radio":
          return "لازم تختار وحدة من الخيارات.";
        case "multiselect":
          return "لازم تختار خيار واحد على الأقل.";
        case "file":
          return "لازم ترفع ملف.";
        default:
          return "هذا الحقل مطلوب.";
      }
    }
    return null;
  }

  switch (input.type) {
    case "text":
    case "textarea": {
      const s = String(value);
      if (input.maxLength && s.length > input.maxLength) {
        return `الحد الأقصى ${input.maxLength} حرف — عندك ${s.length}.`;
      }
      return null;
    }

    case "number": {
      const raw = typeof value === "number" ? String(value) : normalizeDigits(String(value));
      const n = Number(raw);
      if (!Number.isFinite(n)) return "لازم يكون رقم.";
      if (input.min !== undefined && n < input.min) return `أقل قيمة مسموحة ${input.min}.`;
      if (input.max !== undefined && n > input.max) return `أعلى قيمة مسموحة ${input.max}.`;
      return null;
    }

    case "select":
    case "radio": {
      const s = String(value);
      if (input.options && !input.options.some((o) => o.value === s)) {
        return "الخيار المحدد غير صالح.";
      }
      return null;
    }

    case "multiselect": {
      const arr = Array.isArray(value) ? value : [String(value)];
      if (input.options) {
        const allowed = new Set(input.options.map((o) => o.value));
        if (arr.some((v) => !allowed.has(v))) return "أحد الخيارات المحددة غير صالح.";
      }
      return null;
    }

    case "file": {
      // Values carry the file name; the File itself is held outside the value map.
      return null;
    }

    case "toggle":
      return null;

    default:
      return null;
  }
}

/** Validate the whole form. Only visible inputs are considered. */
export function validateAll(inputs: InputDef[], values: FieldValues): Errors {
  const errors: Errors = {};
  for (const input of inputs) {
    const err = validateInput(input, values);
    if (err) errors[input.name] = err;
  }
  return errors;
}

/**
 * The payload actually sent. Hidden inputs are stripped — a value the user
 * couldn't see must never reach the prompt, or `showIf` would be a lie.
 */
export function submittableValues(inputs: InputDef[], values: FieldValues): FieldValues {
  const out: FieldValues = {};
  for (const input of visibleInputs(inputs, values)) {
    const v = values[input.name];
    if (isEmpty(v)) {
      // Toggles are meaningful when false; everything else is just absent.
      if (input.type === "toggle") out[input.name] = Boolean(v);
      continue;
    }
    out[input.name] = input.type === "number" ? Number(normalizeDigits(String(v))) : v;
  }
  return out;
}

/** Seed the form from the pack's declared defaults. */
export function defaultValues(inputs: InputDef[]): FieldValues {
  const out: FieldValues = {};
  for (const input of inputs) {
    if (input.default !== undefined) out[input.name] = input.default;
    else if (input.type === "toggle") out[input.name] = false;
    else if (input.type === "multiselect") out[input.name] = [];
  }
  return out;
}
